import { useEffect, useState } from 'react';
import { fetchChannelMember } from '../api/dataApi';
import { queryClient } from './queryClient';
import { useStore } from '../store/StoreContext';
import { DEFAULT_CHANNEL_MEMBER_PERMISSIONS, type Channel, type ChannelMemberPermissions } from '../types';

export type Membership = 'loading' | 'accepted' | 'none';

// Whether the signed-in account may post into `channel` - the backend gate
// for both signals and reports is "owner or accepted member" (see
// backend/workflows/signal/create.wsl), so a channel reached via Search /
// "Open chat" but not yet followed shows a join prompt instead of a
// composer. Shared by MessagesScreen (signals) and ReportsScreen so both
// panes of the chat screen agree, and routed through queryClient so the
// two of them share one GET /channels/{n}/member/{id} instead of firing
// it twice. 'loading' until the member-doc check resolves, so a composer
// never flashes in first for someone who can't actually use it.
export function useChannelMembership(channel: Channel | undefined): Membership {
  const { isLoggedIn, isChannelOwner, profile, channels } = useStore();
  const [membership, setMembership] = useState<Membership>('loading');

  const nickname = channel?.nickname;
  // A stable primitive for the effect dep - the ownerIds array itself is a
  // fresh reference on every store update.
  const ownerKey = channel?.ownerIds?.join(',');
  const owner = !!channel && isChannelOwner(channel);
  // GET /channels returns exactly "owned + accepted member + followed"
  // (see backend/workflows/channel/list.wsl), so a channel present in the
  // personal list is one this account can post into - and this updates
  // synchronously the moment addChannelToPersonalList runs after a join,
  // without waiting for the member-doc fetch below to re-resolve.
  const inMyChannels = !!channel && channels.some((c) => c.id === channel.id);

  useEffect(() => {
    let cancelled = false;
    if (!channel || !isLoggedIn) {
      setMembership('none');
      return;
    }
    if (owner || inMyChannels) {
      setMembership('accepted');
      return;
    }
    setMembership('loading');
    // profile.userId is '' until the first profile fetch resolves; querying
    // with it now sends GET /channels/{n}/member/ (empty {userId}) which
    // 404s. Stay 'loading' - the effect re-runs once userId lands.
    if (!profile.userId) return;
    queryClient
      .query({
        queryKey: ['channelMemberMe', nickname, profile.userId],
        queryFn: () => fetchChannelMember(nickname!, profile.userId),
        // This branch only runs pre-membership (owner/inMyChannels above
        // already short-circuit once you're in) - a cached miss from
        // before joining must never survive past the join, so this always
        // reaches the network, same as useChannelMemberPermissions' own
        // identical override just below.
        staleTime: 0,
      })
      .then((m) => {
        if (!cancelled) setMembership(m?.status === 'accepted' ? 'accepted' : 'none');
      })
      .catch(() => {
        if (!cancelled) setMembership('none');
      });
    return () => {
      cancelled = true;
    };
    // `channel` read via its primitive fields so this needn't depend on the
    // whole object (which is a fresh reference on every store update).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname, ownerKey, owner, inMyChannels, isLoggedIn, profile.userId]);

  return membership;
}

// This account's effective send permissions for `channel` (roadmap 4a-f).
// The channel owner is unrestricted (returns the permissive defaults
// without a fetch); everyone else gets their materialised
// channel:member:*.permissions. Shares the same
// ['channelMemberMe', nickname, userId] query key as useChannelMembership,
// so the two hooks together still cost one GET /channels/{n}/member/{id}.
// Falls back to permissive defaults while loading or on error, so the
// composer never flash-restricts a member who is actually allowed.
export function useChannelMemberPermissions(channel: Channel | undefined): ChannelMemberPermissions {
  const { isLoggedIn, isChannelOwner, profile, channels } = useStore();
  const [permissions, setPermissions] = useState<ChannelMemberPermissions>(DEFAULT_CHANNEL_MEMBER_PERMISSIONS);

  const nickname = channel?.nickname;
  const ownerKey = channel?.ownerIds?.join(',');
  const owner = !!channel && isChannelOwner(channel);
  // Same signal useChannelMembership uses to flip 'loading'/'none' straight
  // to 'accepted' the instant a join lands - needed here too, and for the
  // same reason: without it, this hook's fetch effect had no dependency
  // that changes when you follow/join, so it fired exactly once (whatever
  // the pre-membership answer was - a 404-derived null, i.e. the
  // permissive DEFAULT_CHANNEL_MEMBER_PERMISSIONS fallback below) and
  // never re-ran, no matter how the account's real membership changed
  // afterward. That's why the composer looked fully open right after
  // pressing Follow - `canPost` (useChannelMembership) correctly flipped
  // to true immediately, but `perms` (this hook) was still the stale
  // pre-join default - and why a plain reload then "fixed" it: only a
  // fresh mount re-ran this effect from scratch against the now-real
  // member doc.
  const inMyChannels = !!channel && channels.some((c) => c.id === channel.id);

  useEffect(() => {
    let cancelled = false;
    if (!channel || !isLoggedIn || owner || !profile.userId) {
      setPermissions(DEFAULT_CHANNEL_MEMBER_PERMISSIONS);
      return;
    }
    queryClient
      .query({
        queryKey: ['channelMemberMe', nickname, profile.userId],
        queryFn: () => fetchChannelMember(nickname!, profile.userId),
        // A cached miss/stale answer from before you joined must never be
        // served after joining - this effect now reruns right on the join
        // (inMyChannels below), so it needs to actually reach the network
        // that moment, not replay whatever the 5s-stale cache still has.
        staleTime: 0,
      })
      .then((m) => {
        if (!cancelled) setPermissions(m?.permissions ?? DEFAULT_CHANNEL_MEMBER_PERMISSIONS);
      })
      .catch(() => {
        if (!cancelled) setPermissions(DEFAULT_CHANNEL_MEMBER_PERMISSIONS);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nickname, ownerKey, owner, inMyChannels, isLoggedIn, profile.userId]);

  return permissions;
}
